import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TrendingUp, TrendingDown, X, Crown, Trophy } from 'lucide-react';
import { hideNativeTabBar, showNativeTabBar } from '../../services/api';
import './RankingModal.css';

const RankingModal = ({ isOpen, onClose, rankingData, onStockClick }) => {
  useEffect(() => {
    if (isOpen) hideNativeTabBar();
    else showNativeTabBar();
    return () => showNativeTabBar();
  }, [isOpen]);

  // 주식 로고 경로 생성
  const getStockLogoPath = (ticker) => {
    return `/CompanyLogo/${ticker}.svg`;
  };

  // 순위 아이콘
  const getRankIcon = (rank) => {
    if (rank === 1) {
      return <Crown className="w-5 h-5 text-yellow-400" fill="currentColor" />;
    } else if (rank === 2) {
      return <Crown className="w-5 h-5 text-blue-200" fill="currentColor" />;
    } else if (rank === 3) {
      return <Crown className="w-5 h-5 text-yellow-200" fill="currentColor" />;
    }
    return null;
  };

  // 점수에 따른 배지 스타일
  const getScoreBadge = (score) => {
    if (score >= 70) {
      return {
        bg: 'bg-green-100',
        text: 'text-green-600',
        border: 'border-green-200',
      };
    } else if (score >= 40) {
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        border: 'border-blue-200',
      };
    } else if (score >= 0) {
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-600',
        border: 'border-gray-200',
      };
    } else if (score >= -40) {
      return {
        bg: 'bg-pink-100',
        text: 'text-red-600',
        border: 'border-red-200',
      };
    } else {
      return {
        bg: 'bg-pink-100',
        text: 'text-red-600',
        border: 'border-red-200',
      };
    }
  };

  // 상태 배지
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'SELL': return { text: '매도', color: 'bg-pink-100 text-red-600' };
      case 'BUY': return { text: '매수', color: 'bg-green-100 text-green-600' };
      case 'HOLDING': return { text: '보유', color: 'bg-blue-50 text-blue-600' };
      case 'NO_POSITION': return { text: '미보유', color: 'bg-gray-100 text-gray-500' };
      default: return { text: '미보유', color: 'bg-gray-100 text-gray-500' };
    }
  };

  // 수익률 색상
  const getProfitColor = (profit) => {
    if (profit > 0) return 'text-blue-600';
    if (profit < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  // 수익률 포맷
  const formatProfit = (profit) => {
    const percent = profit * 100;
    return percent > 0 ? `+${percent.toFixed(2)}%` : `${percent.toFixed(2)}%`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="ranking-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            // className="ranking-modal-content z-[10000]"
            className="far-modal-root fixed inset-0 z-[10000] bg-white flex flex-col overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
          >
        {/* 헤더 */}
        <div className="far-header">
          <div className="far-header-left">
            <span className="far-header-report-type">AI RANKING</span>
            <div className="far-header-title-row">
              <span className="far-header-title">오늘의 AI 주식 랭킹</span>
            </div>
          </div>
          <button onClick={onClose} className="far-close-btn">
            <X size={15} />
          </button>
        </div>

        {/* 랭킹 리스트 */}
        <div className="ranking-modal-body">
          <div className="space-y-3 md:space-y-3">
            {rankingData?.list?.map((stock, index) => {
              const scoreBadge = getScoreBadge(stock.score);
              const statusInfo = getStatusDisplay(stock.status);

              return (
                <div
                  key={stock.ticker}
                  className={`bg-white/80 backdrop-blur-sm rounded-2xl hover:shadow-xl transition-all duration-300 p-3 md:p-5 border cursor-pointer ${
                    index < 3
                      ? 'border-blue-200 shadow-lg shadow-blue-100/50'
                      : 'border-slate-200 shadow-md hover:border-blue-200'
                  }`}
                  onClick={() => {
                    onClose();
                    onStockClick(stock.ticker);
                  }}
                >
                  {/* 모바일 레이아웃 */}
                  <div className="md:hidden">
                    <div className="flex items-center gap-2 mb-2">
                      {/* 순위 */}
                      <div className="flex-shrink-0">
                        {index < 3 ? getRankIcon(index + 1) : <span className="text-lg text-gray-400">#{index + 1}</span>}
                      </div>

                      {/* 로고 */}
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 shadow-md overflow-hidden">
                          <img
                            src={getStockLogoPath(stock.ticker)}
                            alt={stock.ticker}
                            className="w-full h-full object-contain p-1.5"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <span className="text-sm text-gray-400" style={{ display: 'none' }}>
                            {stock.ticker.charAt(0)}
                          </span>
                        </div>
                      </div>

                      {/* 티커 + 상태 */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 truncate">{stock.ticker}</h3>
                        <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium inline-block ${statusInfo.color}`}>
                          {statusInfo.text}
                        </span>
                      </div>

                      {/* 점수 */}
                      <div className="flex-shrink-0 text-right">
                        <div className="text-lg font-bold text-gray-900">{stock.score}점</div>
                      </div>
                    </div>

                    {/* 통계 */}
                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                      <div className="flex flex-col gap-0.5 text-xs text-gray-600">
                        <div>거래 추적일 <span className="font-semibold text-gray-900">{stock.monitoringDayCount}일</span></div>
                        <div className={getProfitColor(stock.totalProfit)}>
                          시뮬레이션 수익률 <span className="font-semibold">{formatProfit(stock.totalProfit)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 데스크톱 레이아웃 */}
                  <div className="hidden md:flex items-center gap-4">
                    {/* 순위 */}
                    <div className="flex items-center justify-center min-w-[50px]">
                      {index < 3 ? (
                        <div className="flex flex-col items-center">
                          {getRankIcon(index + 1)}
                          <span className="text-xs text-gray-500 mt-1">#{index + 1}</span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="text-xl text-gray-400">#{index + 1}</span>
                        </div>
                      )}
                    </div>

                    {/* 로고 */}
                    <div className="relative">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gray-100 shadow-lg overflow-hidden">
                        <img
                          src={getStockLogoPath(stock.ticker)}
                          alt={stock.ticker}
                          className="w-full h-full object-contain p-2"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <span className="text-xl text-gray-400" style={{ display: 'none' }}>
                          {stock.ticker.charAt(0)}
                        </span>
                      </div>
                    </div>

                    {/* 정보 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-gray-900">{stock.ticker}</h3>
                        <span className={`text-xs px-2 py-1 rounded-md font-medium ${statusInfo.color}`}>
                          {statusInfo.text}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span>거래 추적일 {stock.monitoringDayCount}일</span>
                        <span className="text-gray-300">|</span>
                        <span className={getProfitColor(stock.totalProfit)}>
                          시뮬레이션 수익률 {formatProfit(stock.totalProfit)}
                        </span>
                      </div>
                    </div>

                    {/* AI 점수 */}
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900 mb-1">{stock.score}점</div>
                      <div className={`flex items-center justify-end gap-1 text-sm ${
                        stock.score >= 50 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {stock.score >= 50 ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                        <span>AI 분석</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 푸터 */}
        <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <p className="text-xs text-gray-500 text-center">
            AI 점수는 기술적 분석, 펀더멘털, 뉴스, 시장 심리 등을 종합한 투자 참고 지표입니다
          </p>
        </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RankingModal;
